import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import Icon from './icons/Icon';
import './SearchDropdown.css';

/**
 * Generic trigger + search popover, extracted from the original
 * PlaybookPicker.jsx (task: "that dropdown must be reused in Service
 * selection" — the service selector in UnifiedTopBar.jsx was a plain
 * native <select>, visually and functionally inconsistent with the
 * playbook picker's rich search+icon+description popover right next to it
 * in the same header row). Same portal + position:fixed pattern as
 * UserAccountMenu.jsx/BackgroundTasksIndicator.jsx: a trigger living inside
 * a cramped header row needs a portal'd popover, not position:absolute,
 * or it gets clipped/buried under later-painted siblings.
 *
 * `items` is the searchable list (each `{value, icon, title, desc?}`).
 * `pinnedItem` is an optional always-visible, never-filtered row shown
 * above the search results (PlaybookPicker's "Auto", the service picker's
 * "Home"). `trailingItem` is an optional always-visible, never-filtered
 * row shown BELOW the results (the service picker's "+ Create new
 * service").
 */
export default function SearchDropdown({
  triggerIcon,
  triggerLabel,
  triggerTitle,
  triggerClassName,
  triggerStatus,
  triggerCaption,
  disabled,
  items,
  selectedValue,
  onSelect,
  pinnedItem,
  trailingItem,
  searchPlaceholder,
  footer,
  emptyText,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [popoverPos, setPopoverPos] = useState(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const POPOVER_HEIGHT_ESTIMATE = 360;
    const opensUp = rect.top > POPOVER_HEIGHT_ESTIMATE + 16;
    // Keep the popover inside the viewport: a trigger near the right edge (the workspace's service picker)
    // used to push it off screen. Width from the rendered popover, else the CSS width (320px).
    const width = popoverRef.current?.offsetWidth || 320;
    setPopoverPos({
      left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
      ...(opensUp
        ? { bottom: window.innerHeight - rect.top + 8 }
        : { top: rect.bottom + 8 }),
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (e) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const filtered = query.trim()
    ? items.filter((it) => `${it.title} ${it.desc || ''}`.toLowerCase().includes(query.trim().toLowerCase()))
    : items;

  const handlePick = (value) => {
    onSelect(value);
    setIsOpen(false);
    setQuery('');
  };

  // Keyboard: the search box owns the focus, the arrows move a highlight over every row, Enter picks it, Escape closes.
  const rows = [...(pinnedItem ? [pinnedItem] : []), ...filtered, ...(trailingItem ? [trailingItem] : [])];
  const [highlight, setHighlight] = useState(0);
  useEffect(() => { setHighlight(0); }, [query, isOpen]);
  const onSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((i) => Math.min(rows.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Enter') { if (rows[highlight]) { e.preventDefault(); handlePick(rows[highlight].value); } }
    else if (e.key === 'Escape') { e.preventDefault(); setIsOpen(false); setQuery(''); triggerRef.current?.focus(); }
  };
  const rowClass = (row, base) => `${base}${selectedValue === row.value ? ' active' : ''}${rows[highlight]?.value === row.value ? ' is-highlighted' : ''}`;
  const dot = (status) => (status ? <span aria-label={status} className={`sd-dot sd-dot-${status}`} role="img" /> : null);

  return (
    <div className="search-dropdown">
      <button
        type="button"
        className={triggerClassName ? `search-dropdown-trigger ${triggerClassName}` : 'search-dropdown-trigger'}
        ref={triggerRef}
        disabled={disabled}
        title={triggerTitle}
        onClick={() => setIsOpen((v) => !v)}
      >
        {triggerCaption && <span className="search-dropdown-trigger-caption">{triggerCaption}</span>}
        <Icon name={triggerIcon} size={14} />
        {dot(triggerStatus)}
        <span className="search-dropdown-trigger-label">{triggerLabel}</span>
        <span className="search-dropdown-trigger-caret"><Icon name="chevron-down" size={12} /></span>
      </button>
      {isOpen && popoverPos && createPortal(
        <div className="search-dropdown-popover" ref={popoverRef} style={popoverPos}>
          <div className="search-dropdown-search">
            <Icon name="search" size={13} />
            <input
              autoFocus
              type="text"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onSearchKeyDown}
            />
          </div>
          <div className="search-dropdown-list">
            {pinnedItem && (
              <button
                type="button"
                className={rowClass(pinnedItem, 'search-dropdown-item search-dropdown-item-pinned')}
                onClick={() => handlePick(pinnedItem.value)}
              >
                <Icon name={pinnedItem.icon} size={14} />
                <div className="search-dropdown-item-text">
                  <span className="search-dropdown-item-title">{pinnedItem.title}</span>
                  {pinnedItem.desc && <span className="search-dropdown-item-desc">{pinnedItem.desc}</span>}
                </div>
              </button>
            )}
            {filtered.map((it) => (
              <button
                type="button"
                key={it.value}
                className={rowClass(it, 'search-dropdown-item')}
                onClick={() => handlePick(it.value)}
              >
                <Icon name={it.icon} size={14} />
                {dot(it.status)}
                <div className="search-dropdown-item-text">
                  <span className="search-dropdown-item-title">{it.title}</span>
                  {it.desc && <span className="search-dropdown-item-desc">{it.desc}</span>}
                </div>
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="search-dropdown-empty">{emptyText ? emptyText.replace('{query}', query) : `No match for "${query}"`}</div>
            )}
            {trailingItem && (
              <button
                type="button"
                className={rowClass(trailingItem, 'search-dropdown-item search-dropdown-item-trailing')}
                onClick={() => handlePick(trailingItem.value)}
              >
                <Icon name={trailingItem.icon} size={14} />
                <div className="search-dropdown-item-text">
                  <span className="search-dropdown-item-title">{trailingItem.title}</span>
                </div>
              </button>
            )}
          </div>
          {footer && <div className="search-dropdown-footer">{footer}</div>}
        </div>,
        document.body
      )}
    </div>
  );
}

SearchDropdown.propTypes = {
  triggerIcon: PropTypes.string.isRequired,
  triggerLabel: PropTypes.node.isRequired,
  triggerTitle: PropTypes.string,
  triggerClassName: PropTypes.string,
  triggerStatus: PropTypes.string,
  triggerCaption: PropTypes.string,
  disabled: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    desc: PropTypes.string,
    status: PropTypes.string,
  })).isRequired,
  selectedValue: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  pinnedItem: PropTypes.shape({
    value: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    desc: PropTypes.string,
  }),
  trailingItem: PropTypes.shape({
    value: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }),
  searchPlaceholder: PropTypes.string,
  footer: PropTypes.node,
  emptyText: PropTypes.string,
};
